import {
  Alert,
  Box,
  Typography,
  Button as MuiButton,
  CircularProgress,
} from "@mui/material";
import { accountStyles } from "./pageStyles";
import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  getTenantDetails,
  getTenantUsers,
  addTenantUser,
  editTenantUser,
  resetUserPassword,
  getSubscriptionPlans,
  updateSubscription,
  convertTrialToPaid,
  extendTrial,
  seedSubscriptionPlans,
  seedAllBillingData,
  deleteTenantDatabase,
} from "../apiHelper";
import PersonComponent from "../components/PersonComponent";
import AddUserModal from "../components/AddUserModal";
import EditUserModal from "../components/EditUserModal";
import SubscriptionEditForm from "../components/SubscriptionEditForm";
import ConvertTrialCard from "../components/ConvertTrialCard";
import DeleteDatabaseModal from "../components/DeleteDatabaseModal";
import QuestionLibraryImport from "../components/questionLibrary/QuestionLibraryImport";
import { useAuth } from "../contexts/AuthContext";
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Globe,
  CreditCard,
  Activity,
  ArrowLeft,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Settings,
  ChevronRight,
  Save,
  Trash2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const Account = () => {
  const classes = accountStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [accountData, setAccountData] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [provisionMessage, setProvisionMessage] = useState(null);
  const [seedingMessage, setSeedingMessage] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userMessage, setUserMessage] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptionMessage, setSubscriptionMessage] = useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    subscriptionPlan: "",
    billingCycle: "monthly",
    userLicenseCount: 1,
  });
  const [hasSubscriptionChanges, setHasSubscriptionChanges] = useState(false);
  const [convertingTrial, setConvertingTrial] = useState(false);
  const [conversionMessage, setConversionMessage] = useState(null);
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [extendMessage, setExtendMessage] = useState(null);
  const [showDeleteDatabase, setShowDeleteDatabase] = useState(false);
  const [deletingDatabase, setDeletingDatabase] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const { getAccessTokenSilently, isSuperAdmin, hasMinimumRole } = useAuth();

  // Extract current section from URL
  const currentSection = location.pathname.split("/")[2] || "general";

  const handleSectionChange = (sectionId) => {
    navigate(`/${accountId}/${sectionId}`);
  };

  const accountSections = [
    {
      id: "general",
      title: "General Info",
      description: "Account details and company information",
      icon: Building2,
      color: "bg-blue-100 text-blue-800",
    },
    {
      id: "users",
      title: "Users",
      description: "Manage account users and permissions",
      icon: Users,
      color: "bg-purple-100 text-purple-800",
    },
    ...(hasMinimumRole("admin")
      ? [
          {
            id: "admin",
            title: "Admin Tools",
            description: "Administrative tools and utilities",
            icon: Settings,
            color: "bg-yellow-100 text-yellow-800",
          },
        ]
      : []),
  ];

  const renderContent = () => {
    switch (currentSection) {
      case "general":
        return renderGeneralInfo();
      case "users":
        return renderUsers();
      case "admin":
        return renderAdminTools();
      default:
        return renderGeneralInfo();
    }
  };

  const fetchTenantUsers = async (token) => {
    try {
      const userData = await getTenantUsers(token, { accountId });
      if (userData.success) {
        setTenantUsers(userData.data || []);
      }
    } catch (err) {
      console.log("Could not fetch tenant users:", err);
      // If tenant users fetch fails, we'll just show empty users
      setTenantUsers([]);
    }
  };

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessTokenSilently();

        // Fetch account details and tenant users in parallel
        const [accountDataResult, userData] = await Promise.allSettled([
          getTenantDetails(token, { accountId }),
          getTenantUsers(token, { accountId }),
        ]);

        if (accountDataResult.status === "fulfilled") {
          const data = accountDataResult.value;
          setAccountData(data);

          // Initialize subscription form data with actual account data
          setSubscriptionFormData({
            subscriptionPlan: data.subscriptionPlan || "",
            billingCycle: data.billingCycle || "monthly",
            userLicenseCount:
              data.userLicenseCount || data.actualUserCount || 1,
          });
        } else {
          throw new Error(
            accountDataResult.reason?.message || "Failed to load account data"
          );
        }

        if (userData.status === "fulfilled" && userData.value.success) {
          setTenantUsers(userData.value.data || []);
        } else {
          console.log("Could not fetch tenant users:", userData.reason);
          setTenantUsers([]);
        }

        // Fetch subscription plans
        await fetchSubscriptionPlans();
      } catch (err) {
        setError(err.message || "Failed to load account data");
      } finally {
        setLoading(false);
      }
    };

    if (accountId) {
      fetchAccountData();
    }
  }, [accountId, getAccessTokenSilently]);

  const handleSeedPlans = async () => {
    setSeeding(true);
    getAccessTokenSilently().then((t) => {
      seedSubscriptionPlans(t)
        .then((response) => {
          setSeedingMessage("Subscription plans seeded successfully!");
          setSeeding(false);
          setTimeout(() => setSeedingMessage(null), 3000);
        })
        .catch((error) => {
          setSeedingMessage("Failed to seed plans, please try again");
          setSeeding(false);
          setTimeout(() => setSeedingMessage(null), 3000);
        });
    });
  };

  const handleSeedAllData = async () => {
    setSeeding(true);
    getAccessTokenSilently().then((t) => {
      seedAllBillingData(t)
        .then((response) => {
          setSeedingMessage("All billing data seeded successfully!");
          setSeeding(false);
          setTimeout(() => setSeedingMessage(null), 3000);
        })
        .catch((error) => {
          setSeedingMessage("Failed to seed billing data, please try again");
          setSeeding(false);
          setTimeout(() => setSeedingMessage(null), 3000);
        });
    });
  };

  const handleAddUser = async (userData) => {
    try {
      setUserMessage(null);
      const token = await getAccessTokenSilently();
      const result = await addTenantUser(token, {
        accountId,
        ...userData,
      });

      if (result.success) {
        setUserMessage({ type: "success", text: "User added successfully!" });
        setShowAddUser(false);
        // Refresh users list
        const userData = await getTenantUsers(token, { accountId });
        if (userData.success) {
          setTenantUsers(userData.data || []);
        }
      } else {
        setUserMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setUserMessage({ type: "error", text: error.message });
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      setUserMessage(null);
      const token = await getAccessTokenSilently();
      const result = await editTenantUser(token, userId, {
        accountId,
        ...userData,
      });

      if (result.success) {
        setUserMessage({ type: "success", text: "User updated successfully!" });
        setEditingUser(null);
        // Refresh users list
        const userData = await getTenantUsers(token, { accountId });
        if (userData.success) {
          setTenantUsers(userData.data || []);
        }
      } else {
        setUserMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setUserMessage({ type: "error", text: error.message });
    }
  };

  const handleResetPassword = async (email) => {
    try {
      setUserMessage(null);
      const token = await getAccessTokenSilently();
      const result = await resetUserPassword(token, { email });

      if (result.success) {
        setUserMessage({ type: "success", text: "Password reset email sent!" });
      } else {
        setUserMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setUserMessage({ type: "error", text: error.message });
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      setSubscriptionMessage(null);
      const token = await getAccessTokenSilently();

      console.log("Sending subscription update:", subscriptionFormData);

      const result = await updateSubscription(
        token,
        accountId,
        subscriptionFormData
      );

      console.log("Subscription update result:", result);

      if (result.success) {
        setSubscriptionMessage({
          type: "success",
          text: "Subscription updated successfully!",
        });
        setHasSubscriptionChanges(false);
        // Refresh account data
        const accountData = await getTenantDetails(token, { accountId });
        setAccountData(accountData);

        // Update the form data with the refreshed account data
        setSubscriptionFormData({
          subscriptionPlan: accountData.subscriptionPlan || "",
          billingCycle: accountData.billingCycle || "monthly",
          userLicenseCount:
            accountData.userLicenseCount || accountData.actualUserCount || 1,
        });

        setTimeout(() => setSubscriptionMessage(null), 3000);
      } else {
        setSubscriptionMessage({ type: "error", text: result.message });
      }
    } catch (error) {
      setSubscriptionMessage({ type: "error", text: error.message });
    }
  };

  const handleSubscriptionChange = (field, value) => {
    // Create the new form data with the updated field
    const newFormData = {
      ...subscriptionFormData,
      [field]: value,
    };

    setSubscriptionFormData(newFormData);

    // Check if there are changes using the NEW form data (not the old state)
    // Ensure we're comparing the same types (convert to numbers for userLicenseCount)
    const hasChanges =
      newFormData.subscriptionPlan !== (accountData.subscriptionPlan || "") ||
      newFormData.billingCycle !== (accountData.billingCycle || "monthly") ||
      Number(newFormData.userLicenseCount) !==
        Number(
          accountData.userLicenseCount || accountData.actualUserCount || 1
        );

    setHasSubscriptionChanges(hasChanges);
  };

  const handleConvertTrial = async (conversionData) => {
    try {
      setConvertingTrial(true);
      setConversionMessage(null);
      const token = await getAccessTokenSilently();

      console.log("Converting trial to paid:", conversionData);

      const result = await convertTrialToPaid(token, accountId, conversionData);

      console.log("Trial conversion result:", result);

      if (result.success) {
        setConversionMessage({
          type: "success",
          text: "Trial account successfully converted to paid account!",
        });

        // Refresh account data to show updated information
        const updatedAccountData = await getTenantDetails(token, { accountId });
        setAccountData(updatedAccountData);

        // Update subscription form data
        setSubscriptionFormData({
          subscriptionPlan: updatedAccountData.subscriptionPlan || "",
          billingCycle: updatedAccountData.billingCycle || "monthly",
          userLicenseCount:
            updatedAccountData.userLicenseCount ||
            updatedAccountData.actualUserCount ||
            1,
        });

        // Clear success message after 5 seconds
        setTimeout(() => setConversionMessage(null), 5000);
      } else {
        setConversionMessage({
          type: "error",
          text: result.message || "Failed to convert trial account",
        });
      }
    } catch (error) {
      console.error("Trial conversion error:", error);
      setConversionMessage({
        type: "error",
        text: error.message || "An error occurred while converting the account",
      });
    } finally {
      setConvertingTrial(false);
    }
  };

  const handleExtendTrial = async (extensionDays) => {
    try {
      setExtendingTrial(true);
      setExtendMessage(null);
      const token = await getAccessTokenSilently();

      console.log("Extending trial by days:", extensionDays);

      const result = await extendTrial(token, accountId, { extensionDays });

      console.log("Trial extension result:", result);

      if (result.success) {
        setExtendMessage({
          type: "success",
          text: `Trial period extended by ${extensionDays} days successfully!`,
        });

        // Refresh account data to show updated trial end date
        const updatedAccountData = await getTenantDetails(token, { accountId });
        setAccountData(updatedAccountData);

        // Clear success message after 5 seconds
        setTimeout(() => setExtendMessage(null), 5000);
      } else {
        setExtendMessage({
          type: "error",
          text: result.message || "Failed to extend trial period",
        });
      }
    } catch (error) {
      console.error("Trial extension error:", error);
      setExtendMessage({
        type: "error",
        text: error.message || "An error occurred while extending the trial",
      });
    } finally {
      setExtendingTrial(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await getSubscriptionPlans(token);
      if (result.success) {
        setSubscriptionPlans(result.data || []);
      }
    } catch (error) {
      console.log("Could not fetch subscription plans:", error);
    }
  };

  const handleDeleteDatabase = async () => {
    try {
      setDeletingDatabase(true);
      setDeleteMessage(null);
      const token = await getAccessTokenSilently();

      console.log("Deleting database:", accountId);

      const result = await deleteTenantDatabase(token, accountId);

      console.log("Database deletion result:", result);

      if (result.success) {
        setDeleteMessage({
          type: "success",
          text: result.message || "Database deleted successfully!",
        });

        // Redirect to accounts page after successful deletion
        setTimeout(() => {
          navigate("/accounts");
        }, 3000);
      } else {
        setDeleteMessage({
          type: "error",
          text: result.message || "Failed to delete database",
        });
      }
    } catch (error) {
      console.error("Database deletion error:", error);
      setDeleteMessage({
        type: "error",
        text: error.message || "An error occurred while deleting the database",
      });
    } finally {
      setDeletingDatabase(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-96">
          <div className="text-center">
            <CircularProgress size={48} />
            <Typography variant="h6" className="mt-4">
              Loading account details...
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <MuiButton
              startIcon={<ArrowLeft />}
              onClick={() => window.history.back()}
              variant="outlined"
            >
              Back
            </MuiButton>
            <Typography variant="h4">Account Details</Typography>
          </div>
          <Alert severity="error">{error}</Alert>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500 text-white";
      case "inactive":
        return "bg-gray-500 text-white";
      case "suspended":
        return "bg-red-500 text-white";
      default:
        return "bg-blue-500 text-white";
    }
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType) {
      case "trial":
        return "bg-amber-500 text-white";
      case "standard":
        return "bg-blue-500 text-white";
      case "premium":
        return "bg-purple-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const renderGeneralInfo = () => {
    const isTrial = accountData.accountType === "trial" || accountData.isTrial;

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Account Type</div>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">
                {accountData.subscriptionPlan ||
                  accountData.accountType ||
                  "Unknown"}
              </div>
              <p className="text-xs text-muted-foreground">Current plan</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Status</div>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">
                {accountData.status || "Unknown"}
              </div>
              <p className="text-xs text-muted-foreground">Account status</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">Users</div>
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${
                  accountData.accountType === "trial" ||
                  !accountData.userLicenseCount
                    ? "text-blue-600"
                    : (tenantUsers?.length || 0) >= accountData.userLicenseCount
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {tenantUsers?.length || 0}/
                {accountData.accountType === "trial" ||
                !accountData.userLicenseCount
                  ? "Unlimited"
                  : accountData.userLicenseCount}
              </div>
              <p className="text-xs text-muted-foreground">Total members</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-2">
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm font-medium">
                  {accountData.accountType === "trial"
                    ? "Trial Days Left"
                    : "Billing Status"}
                </div>
                {accountData.accountType === "trial" ? (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {accountData.accountType === "trial"
                  ? accountData.trialEndDate
                    ? (() => {
                        const today = new Date();
                        const trialEnd = new Date(accountData.trialEndDate);
                        const diffTime = trialEnd - today;
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        );
                        return diffDays > 0 ? `${diffDays}` : "0";
                      })()
                    : "N/A"
                  : accountData.billingStatus || "Active"}
              </div>
              <p className="text-xs text-muted-foreground">
                {accountData.accountType === "trial"
                  ? "Days remaining"
                  : "Current status"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* For trial accounts, show conversion section prominently after stats */}
        {isTrial && (
          <div className="my-8">
            <ConvertTrialCard
              accountData={accountData}
              subscriptionPlans={subscriptionPlans}
              onConvert={handleConvertTrial}
              onExtendTrial={handleExtendTrial}
              loading={convertingTrial}
              extendingTrial={extendingTrial}
              message={conversionMessage}
              extendMessage={extendMessage}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company Information */}
          <Card className="lg:col-span-2 border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-4 lg:pb-6">
              <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Building2 className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <span className="text-base lg:text-xl">
                  Company Information
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Company Name
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {accountData.companyName || "N/A"}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Industry
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {accountData.industry || "N/A"}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Company Size
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {accountData.companySize || "N/A"}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Website
                  </Typography>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <Typography variant="body1" className="font-medium">
                      {accountData.website ? (
                        <a
                          href={accountData.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {accountData.website}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Status */}
          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-4 lg:pb-6">
              <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                  <Shield className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <span className="text-base lg:text-xl">Account Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Typography variant="body2" className="text-gray-500">
                    Status
                  </Typography>
                  <Badge
                    variant="outline"
                    className={`${getStatusColor(accountData.status)} border-0`}
                  >
                    {accountData.status || "Unknown"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <Typography variant="body2" className="text-gray-500">
                    Account Type
                  </Typography>
                  <Badge
                    variant="outline"
                    className={`${getAccountTypeColor(
                      accountData.accountType
                    )} border-0`}
                  >
                    {accountData.accountType || "Unknown"}
                  </Badge>
                </div>
                {accountData.isTrial && (
                  <div className="flex items-center justify-between">
                    <Typography variant="body2" className="text-gray-500">
                      Trial End
                    </Typography>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 text-yellow-500" />
                      <Typography variant="body2" className="font-medium">
                        {accountData.trialEndDate
                          ? new Date(
                              accountData.trialEndDate
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <Typography variant="body2" className="text-gray-500">
                    Created
                  </Typography>
                  <Typography variant="body2" className="font-medium">
                    {accountData.createdAt
                      ? new Date(accountData.createdAt).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </div>
                {accountData.lastLogin && (
                  <div className="flex items-center justify-between">
                    <Typography variant="body2" className="text-gray-500">
                      Last Login
                    </Typography>
                    <Typography variant="body2" className="font-medium">
                      {new Date(accountData.lastLogin).toLocaleDateString()}
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="lg:col-span-2 border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-4 lg:pb-6">
              <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <User className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <span className="text-base lg:text-xl">
                  Contact Information
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Contact Person
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {accountData.contactPersonFirstName &&
                    accountData.contactPersonLastName
                      ? `${accountData.contactPersonFirstName} ${accountData.contactPersonLastName}`
                      : "N/A"}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Email
                  </Typography>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <Typography variant="body1" className="font-medium">
                      {accountData.contactEmail || "N/A"}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Phone
                  </Typography>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <Typography variant="body1" className="font-medium">
                      {accountData.contactPhone || "N/A"}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Lead Source
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {accountData.leadSource || "N/A"}
                  </Typography>
                </div>
              </div>
              {accountData.address && (
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    Address
                  </Typography>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      {accountData.address.street && (
                        <Typography variant="body1">
                          {accountData.address.street}
                        </Typography>
                      )}
                      {accountData.address.city &&
                        accountData.address.state && (
                          <Typography variant="body1">
                            {accountData.address.city},{" "}
                            {accountData.address.state}{" "}
                            {accountData.address.postalCode}
                          </Typography>
                        )}
                      {accountData.address.country && (
                        <Typography variant="body1">
                          {accountData.address.country}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Subscription Section - Only for paid accounts */}
          {!isTrial && (
            <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
              <CardHeader className="pb-4 lg:pb-6">
                <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                    <CreditCard className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <span className="text-base lg:text-xl">Subscription</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Subscription Message */}
                {subscriptionMessage && (
                  <Alert
                    severity={
                      subscriptionMessage.type === "success"
                        ? "success"
                        : "error"
                    }
                    className="mb-4"
                  >
                    {subscriptionMessage.text}
                  </Alert>
                )}

                {/* Inline Editable Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label
                      htmlFor="subscriptionPlan"
                      className="text-sm font-medium text-gray-700"
                    >
                      Subscription Plan
                    </Label>
                    <Select
                      value={subscriptionFormData.subscriptionPlan}
                      onValueChange={(value) =>
                        handleSubscriptionChange("subscriptionPlan", value)
                      }
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue placeholder="Select plan">
                          {subscriptionFormData.subscriptionPlan ||
                            "Select plan"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {subscriptionPlans.map((plan) => (
                          <SelectItem key={plan._id} value={plan.name}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">{plan.name}</span>
                              <span className="text-gray-500 text-sm">
                                ${plan.price}/{plan.billingCycle}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="billingCycle"
                      className="text-sm font-medium text-gray-700"
                    >
                      Billing Cycle
                    </Label>
                    <Select
                      value={subscriptionFormData.billingCycle}
                      onValueChange={(value) =>
                        handleSubscriptionChange("billingCycle", value)
                      }
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue placeholder="Select billing cycle">
                          {subscriptionFormData.billingCycle
                            ? subscriptionFormData.billingCycle
                                .charAt(0)
                                .toUpperCase() +
                              subscriptionFormData.billingCycle.slice(1)
                            : "Select billing cycle"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="userLicenseCount"
                    className="text-sm font-medium text-gray-700"
                  >
                    User License Count
                  </Label>
                  <Input
                    id="userLicenseCount"
                    type="number"
                    min="1"
                    value={subscriptionFormData.userLicenseCount}
                    onChange={(e) =>
                      handleSubscriptionChange(
                        "userLicenseCount",
                        parseInt(e.target.value) || 1
                      )
                    }
                    className="mt-1"
                  />
                </div>

                {/* Read-only fields */}
                {accountData.domain && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Domain
                    </Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md font-mono text-sm">
                      {accountData.domain}
                    </div>
                  </div>
                )}
                {accountData.subdomain && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Subdomain
                    </Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded-md font-mono text-sm">
                      {accountData.subdomain}
                    </div>
                  </div>
                )}

                {/* Save Button - Only show when there are changes */}
                {hasSubscriptionChanges && (
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleUpdateSubscription}
                      className="bg-[#00C68D] hover:bg-[#01C891] text-white font-medium"
                    >
                      Save Changes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Notes Section */}
        {accountData.notes && (
          <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
            <CardHeader className="pb-4 lg:pb-6">
              <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                  <Activity className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                </div>
                <span className="text-base lg:text-xl">Notes</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Typography
                variant="body1"
                className="text-gray-700 whitespace-pre-wrap"
              >
                {accountData.notes}
              </Typography>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderUsers = () => (
    <div className="space-y-6">
      {/* User Management Header */}
      <Card className="border-0 shadow-lg shadow-gray-200/50 bg-white/80 backdrop-blur-sm hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
        <CardHeader className="pb-4 lg:pb-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                <Users className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Account Users</span>
            </CardTitle>
            <Button
              onClick={() => setShowAddUser(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <User className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* User Message */}
          {userMessage && (
            <Alert
              severity={userMessage.type === "success" ? "success" : "error"}
              className="mb-4"
            >
              {userMessage.text}
            </Alert>
          )}

          {tenantUsers && tenantUsers.length > 0 ? (
            <div className="border-0 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-gray-700">
                      Person
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Role
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Status
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Contact
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantUsers.map((person, i) => (
                    <TableRow
                      key={i}
                      className="border-b border-gray-50 hover:bg-gray-50/30 transition-colors"
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {person.name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase() || "U"}
                          </div>
                          <div>
                            <div className="font-medium">
                              {person.name || "Unknown User"}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-800"
                        >
                          {person.role || "user"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="secondary"
                          className={
                            person.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }
                        >
                          {person.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-3 w-3 mr-1" />
                            {person.email || "No email"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(person)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetPassword(person.email)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          >
                            Reset Password
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <Typography variant="h6" className="text-gray-500 mb-2">
                No users found
              </Typography>
              <Typography variant="body2" className="text-gray-400 mb-4">
                This account doesn't have any associated users yet.
              </Typography>
              <Button
                onClick={() => setShowAddUser(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <User className="h-4 w-4 mr-2" />
                Add First User
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Modal */}
      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onSave={handleAddUser}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(userData) => handleEditUser(editingUser._id, userData)}
        />
      )}
    </div>
  );

  const renderAdminTools = () => (
    <div className="space-y-6">
      {/* Question Library Import - Admin and Super Admin */}
      <QuestionLibraryImport accountId={accountId} />

      {/* Billing Data Seeding - Super Admin Only */}
      {isSuperAdmin() && (
        <Card className="border-0 shadow-lg shadow-gray-200/50 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl text-yellow-800">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/25">
                <Settings className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Billing Data Seeding</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="body2" className="text-yellow-700 mb-4">
              Use these tools to seed billing data for testing purposes.
            </Typography>
            <div className="flex gap-3 flex-wrap">
              <MuiButton
                variant="outlined"
                color="primary"
                onClick={handleSeedPlans}
                disabled={seeding}
                startIcon={seeding ? <CircularProgress size={16} /> : null}
                className="rounded-xl"
              >
                {seeding ? "Seeding..." : "Seed Plans"}
              </MuiButton>
              <MuiButton
                variant="outlined"
                color="secondary"
                onClick={handleSeedAllData}
                disabled={seeding}
                startIcon={seeding ? <CircularProgress size={16} /> : null}
                className="rounded-xl"
              >
                {seeding ? "Seeding..." : "Seed All Data"}
              </MuiButton>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Database Management - Super Admin Only */}
      {isSuperAdmin() && (
        <Card className="border-0 shadow-lg shadow-gray-200/50 bg-gradient-to-r from-red-50 to-pink-50 border-red-200 hover:shadow-xl hover:shadow-gray-300/50 transition-all duration-300">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl text-red-800">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/25">
                <Trash2 className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Database Management</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="body2" className="text-red-700 mb-4">
              <strong>Danger Zone:</strong> This will permanently delete the
              entire database and all associated data including Auth0 users and
              organization.
            </Typography>

            <div className="flex gap-3 flex-wrap">
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDatabase(true)}
                disabled={deletingDatabase}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Database
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Tools Info - Shown to Admin users when no Super Admin tools are visible */}
      {!isSuperAdmin() && (
        <Card className="border-0 shadow-lg shadow-gray-200/50 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl text-blue-800">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Settings className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Admin Tools</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="body2" className="text-blue-700 mb-4">
              Administrative tools and utilities are available here. Additional
              tools may be available to Super Administrators.
            </Typography>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Typography variant="body2" className="text-blue-800">
                <strong>Note:</strong> Some advanced administrative functions
                (such as database deletion and data seeding) are restricted to
                Super Administrators only.
              </Typography>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                {accountData.accountName || "Account Details"}
              </h1>
              <p className="text-sm lg:text-base text-gray-600">
                Database: {accountData.name}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge
              variant="outline"
              className={`${getStatusColor(accountData.status)} border-0`}
            >
              {accountData.status || "Unknown"}
            </Badge>
            <Badge
              variant="outline"
              className={`${getAccountTypeColor(
                accountData.accountType
              )} border-0`}
            >
              {accountData.accountType || "Unknown"}
            </Badge>
          </div>
        </div>

        {/* Messages */}
        {provisionMessage && (
          <Alert
            severity={
              provisionMessage.includes("Email sent successfully to")
                ? "success"
                : "error"
            }
            className="rounded-xl"
          >
            {provisionMessage}
          </Alert>
        )}
        {seedingMessage && (
          <Alert
            severity={
              seedingMessage.includes("successfully") ? "success" : "error"
            }
            className="rounded-xl"
          >
            {seedingMessage}
          </Alert>
        )}

        {/* Section Overview Cards */}
        <div
          className={`grid gap-4 ${
            accountSections.length === 3
              ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
              : "grid-cols-1 md:grid-cols-2 xl:grid-cols-2"
          }`}
        >
          {accountSections.map((section) => {
            const Icon = section.icon;
            const isActive = currentSection === section.id;

            return (
              <Card
                key={section.id}
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  isActive ? "ring-2 ring-blue-500 shadow-md" : ""
                }`}
                onClick={() => handleSectionChange(section.id)}
              >
                <CardHeader className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${section.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {section.title}
                        </CardTitle>
                        <CardDescription className="text-sm">
                          {section.description}
                        </CardDescription>
                      </div>
                    </div>
                    <ChevronRight
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        isActive ? "rotate-90" : ""
                      }`}
                    />
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="mt-6">{renderContent()}</div>
      </div>

      {/* Delete Database Modal */}
      <DeleteDatabaseModal
        isOpen={showDeleteDatabase}
        onClose={() => {
          setShowDeleteDatabase(false);
          setDeleteMessage(null); // Clear message when closing
        }}
        onConfirm={handleDeleteDatabase}
        accountData={accountData}
        loading={deletingDatabase}
        message={deleteMessage}
      />
    </div>
  );
};

export default Account;
